/**
 * Entity Explorer API Service
 * Handles all backend communication and normalization
 * All functions return consistent ExplorerNode[] shape
 */

import { authStore } from '@services/authStore.service';
import { environment } from '@config/environment.config';
import type {
  ExplorerNode,
  BreadcrumbNode,
} from '../types/entity-explorer.types';

/**
 * API error structure
 */
export interface ApiError extends Error {
  status?: number;
  originalError?: unknown;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
}

export interface PagedExplorerNodes {
  items: ExplorerNode[];
  pagination: PaginationMeta;
}

/**
 * Generic POST helper following existing editorApi pattern
 */
async function post<T>(endpoint: string, body: Record<string, any>): Promise<T> {
  const headers = new Headers({
    'Content-Type': 'application/json',
    ...authStore.getAuthHeader(),
  });

  const res = await fetch(
    `${environment.getIlaApiBaseUrl()}${endpoint}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
  );

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    const apiError = new Error(
      error.message || `HTTP ${res.status}`
    ) as ApiError;
    apiError.status = res.status;
    throw apiError;
  }

  // Handle empty response bodies
  const text = await res.text();
  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch (e) {
    console.error('Failed to parse response:', text);
    throw new Error('Invalid JSON response from server');
  }
}

/**
 * Fetch experiment by ID to get description and tags
 * POST /api/experiments/get/experiment
 */
async function fetchExperiment(experimentId: string, entityType: string = 'WETLABWORKFLOW'): Promise<any> {
  try {
    return await post(`/api/experiments/get/experiment`, {
      experiment: experimentId,
      entityType,
      isArchived: false,
    });
  } catch (err) {
    return null;
  }
}

/**
 * Fetch root folders (home level)
 * POST /api/users/list/notebook
 */
export async function fetchRootFolders(opts: {
  showArchived?: boolean;
  skipAudit?: boolean;
  forLinkEntity?: boolean;
  entityTypes: string[];
  selectFolderOnly?: boolean;
  page: number;
  limit: number;
  sortField: 'name' | 'entityType' | 'createdBy' | 'createdAt';
  sortOrder: 'asc' | 'desc';
  searchTerm?: string;
}): Promise<PagedExplorerNodes> {
  const sortFieldMap: Record<string, string> = {
    name: 'name',
    entityType: 'fileType',
    createdBy: 'createdBy.name',
    createdAt: 'creationTime',
  };

  const filters = opts.searchTerm?.trim()
    ? { name: [{ value: opts.searchTerm.trim(), matchMode: 'contains', operator: 'and' }] }
    : {};

  const res = await post<any>('/api/users/list/notebook-entities', {
    skipAudit: opts.skipAudit,
    forLinkEntity: opts.forLinkEntity === true,
    page: opts.page,
    limit: opts.limit,
    sortField: sortFieldMap[opts.sortField] || 'name',
    sortOrder: opts.sortOrder === 'asc' ? 1 : -1,
    filters,
    mode: 'allProjects',
  });

  const items = Array.isArray(res) ? res : (res?.data || res?.result || res?.items || []);
  const total = typeof res?.total === 'number' ? res.total : items.length;
  const page = typeof res?.page === 'number' ? res.page : opts.page;
  const limit = typeof res?.limit === 'number' ? res.limit : opts.limit;

  // Backend already returns only top-level entities — no client-side parentId filter needed.
  const normalizedItems = items
    .filter((e: any) => (opts.showArchived === false ? !e.isArchived : true))
    .map((e: any) => {
      const entityType = e.dataFileType || e.entityType || 'FOLDER';
      const isFolder = entityType === 'FOLDER' || entityType === 'FLEXI_FOLDER';

      return {
        id: e._id,
        name: e.dataFileName || e.fullName || e.name,
        entityType,
        kind: isFolder ? ('folder' as const) : ('entity' as const),
        createdBy: e.createdBy,
        createdAt: e.createdOn || e.createdAt,
        raw: e,
      };
    })
    .filter((node: any) => !opts.selectFolderOnly || node.kind === 'folder');

  return {
    items: normalizedItems,
    pagination: {
      page,
      limit,
      total,
    },
  };
}

/**
 * Fetch folder children (folders + configured entity types)
 * POST /api/flexiFolder/list
 * Note: Backend only filters by parentId, we filter types client-side
 */
export async function fetchFolderChildren(
  parentId: string,
  parentEntityType: string,
  opts: {
    entityTypes: string[];
    showArchived?: boolean;
    skipAudit?: boolean;
    forLinkEntity?: boolean;
    selectFolderOnly?: boolean;
  }
): Promise<ExplorerNode[]> {
  const res = await post<any>('/api/flexiFolder/list', {
    parentId,
    parentEntityType,
    skipAudit: opts.skipAudit,
    forLinkEntity: opts.forLinkEntity === true,
  });

  // Handle both array and object responses
  const items = Array.isArray(res) ? res : (res?.data || res?.result || res?.items || []);

  if (!items || items.length === 0) {
    return [];
  }

  return items
    .filter((e: any) => {
      const isArchived = opts.showArchived === false && e.isArchived;
      return !isArchived;
    })
    .sort((a: any, b: any) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    })
    .map((e: any) => {
      const isFolder = e.dataFileType === 'FLEXI_FOLDER';

      return {
        id: e._id,
        name: e.dataFileName || e.name,
        entityType: e.dataFileType,
        kind: isFolder ? ('folder' as const) : ('entity' as const),
        createdBy: e.createdBy,
        createdAt: e.createdAt,
        raw: e,
      };
    })
    .filter((node: any) => {
      if (node.kind === 'folder') return true;
      return opts.entityTypes.includes(node.entityType);
    })
    .filter((node: any) => !opts.selectFolderOnly || node.kind === 'folder');
}

/**
 * Fetch versions for a given entity (text file, spreadsheet, or workflow)
 * Dispatches to the right endpoint based on entityType
 */
export async function fetchEntityVersions(
  entityType: string,
  entity: ExplorerNode,
  opts?: { excludeVersionId?: string; forLinkEntity?: boolean }
): Promise<ExplorerNode[]> {
  let endpoint = '';
  let body: Record<string, any> = {};
  let resultType = `${entityType}_VERSION`;

  if (entityType === 'TEXTFILE') {
    endpoint = '/api/textFileVersion/get/textFile';
    const entityId = (entity.raw.meta as any)?.properties?.entityId ?? entity.raw._id;
    body = { textFileId: entityId, forLinkEntity: opts?.forLinkEntity === true };
    resultType = 'TEXTFILE_VERSION';
  } else if (entityType === 'SPREADSHEETFILE') {
    endpoint = '/api/spreadsheetFileVersion/get/spreadsheetFile';
    const entityId = (entity.raw.meta as any)?.properties?.entityId ?? entity.raw._id;
    body = { spreadsheetFileId: entityId, forLinkEntity: opts?.forLinkEntity === true };
    resultType = 'SPREADSHEETFILE_VERSION';
  } else if (
    entityType === 'WETLABWORKFLOW' ||
    entityType === 'COMPUTATIONALWORKFLOW'
  ) {
    // Use v2 endpoint with proper pagination
    endpoint = '/api/v2/version/list';
    const experimentId = (entity.raw.meta as any)?.properties?.entityId ?? entity.raw._id;
    body = { experimentId, page: 1, pageSize: 100, forLinkEntity: opts?.forLinkEntity === true };
    resultType = `${entityType}_VERSION`;
  }

  if (!endpoint) return [];

  try {
    const res = await post<any>(endpoint, body);

    // Handle v2 response format: {items: [...], total, page, pageSize}
    // and v1 response format: {version: [...]} or direct array
    let items: any[] = [];
    
    if (Array.isArray(res)) {
      items = res;
    } else if (res?.items && Array.isArray(res.items)) {
      // v2 format
      items = res.items;
    } else if (res?.version && Array.isArray(res.version)) {
      // v1 format
      items = res.version;
    } else if (res?.data && Array.isArray(res.data)) {
      items = res.data;
    } else if (res?.result && Array.isArray(res.result)) {
      items = res.result;
    }

    // Handle nested entity format: [{ entity: {...} }] => [{...}]
    if (items.length > 0 && items[0].entity && !items[0]._id) {
      items = items.map((item: any) => item.entity);
    }

    if (items.length === 0) {
      console.warn(`[fetchEntityVersions] No versions found for ${entityType}:`, { endpoint, body, response: res });
    }

    // For workflow versions, fetch experiment details to get description/tags
    let experimentCache: Record<string, any> = {};
    if ((entityType === 'WETLABWORKFLOW' || entityType === 'COMPUTATIONALWORKFLOW') && items.length > 0) {
      const experimentIds = items
        .map((v) => v.experiment)
        .filter((id) => typeof id === 'string' && id)
        .filter((id, idx, arr) => arr.indexOf(id) === idx); // unique only
      
      if (experimentIds.length > 0) {
        const experiments = await Promise.all(
          experimentIds.map((id) => fetchExperiment(id, entityType))
        );
        experimentIds.forEach((id, idx) => {
          const rawResponse = experiments[idx];
          const unwrappedData = rawResponse?.entity || rawResponse;
          experimentCache[id] = rawResponse;
        });
      }
    }

    return items
      .filter((v) => v._id !== opts?.excludeVersionId)
      .map((v) => {
        const fullName =
          entity.name && v.name
            ? `${entity.name} ${v.name}`
            : v.fullName || v.name;

        // For workflow versions, inherit description and tags from experiment
        const rawData = { ...v };
        if (
          (entityType === 'WETLABWORKFLOW' || entityType === 'COMPUTATIONALWORKFLOW') &&
          v.experiment &&
          typeof v.experiment === 'string'
        ) {
          // Get experiment data from cache (fetched earlier)
          // API returns {entity: {...}} structure, so unwrap it
          let experimentData = experimentCache[v.experiment];
          if (experimentData?.entity) {
            experimentData = experimentData.entity;
          }
          if (experimentData) {
            if (!rawData.description && experimentData.description) {
              rawData.description = experimentData.description;
            }
            if ((!rawData.tags || rawData.tags.length === 0) && experimentData.tags?.length > 0) {
              rawData.tags = experimentData.tags;
            }
          }
        }

        return {
          id: v._id,
          name: fullName,
          entityType: resultType,
          kind: 'version' as const,
          createdBy: v.createdBy,
          createdAt: v.createdAt,
          raw: rawData,
        };
      });
  } catch (err) {
    console.error(`[fetchEntityVersions] Failed to fetch versions:`, { endpoint, body, error: err });
    return [];
  }
}

/**
 * Fetch breadcrumb trail for a node (API-resolved)
 * POST /api/breadcrumb/get
 * Falls back gracefully if entity not found
 */
export async function fetchBreadcrumb(
  entityId: string | null,
  entityType: string,
  isArchived?: boolean,
  forLinkEntity?: boolean
): Promise<BreadcrumbNode[]> {
  if (entityType === 'HOME' || !entityId) {
    return [
      {
        _id: null,
        name: 'Folders',
        entityType: 'HOME',
      },
    ];
  }

  try {
    // The entityId passed should match the Breadcrumb schema's entityId
    // For folders: pass the folder document ID
    // For entities (workflows, text files, etc): pass the entity document ID
    console.log('[fetchBreadcrumb] Fetching breadcrumb for:', { entityId, entityType });
    
    const res = await post<any[]>('/api/breadcrumb/get', {
      entityId,
      entityType,
      isArchived,
      forLinkEntity: forLinkEntity === true,
    });

    console.log('[fetchBreadcrumb] Response:', res);

    const breadcrumbs = (Array.isArray(res) ? res : ((res as any)?.data || (res as any)?.result || (res as any)?.items || (res as any)?.pathData || []));
    
    return breadcrumbs.map((e: any) => ({
      _id: e.entityId || e._id,
      name: e.entityName || e.name,
      entityType: e.entityType,
    }));
  } catch (err) {
    // Graceful degradation: return single node if API fails (e.g., entity not found)
    console.warn('[fetchBreadcrumb] API failed, using fallback:', { entityId, entityType, error: err });
    return [
      {
        _id: entityId,
        name: 'Current',
        entityType: entityType,
      },
    ];
  }
}

/**
 * Search notebook for text files, spreadsheets, workflows
 * POST /api/version/search with client-side type filtering
 */
export async function searchNotebook(
  searchTerm: string,
  entityTypes: string[],
  opts?: { forLinkEntity?: boolean }
): Promise<ExplorerNode[]> {
  if (!searchTerm.trim()) return [];

  try {
    const res = await post<any>('/api/version/search', {
      searchTerm,
      searchScope: 'private',
      page: 1,
      pageSize: 100,
      forLinkEntity: opts?.forLinkEntity === true,
    });

    // Handle both array and object responses
    let items: any[] = [];
    
    if (Array.isArray(res)) {
      items = res;
    } else if (res?.data && Array.isArray(res.data)) {
      items = res.data;
    } else if (res?.result && Array.isArray(res.result)) {
      items = res.result;
    } else if (res?.items && Array.isArray(res.items)) {
      items = res.items;
    } else if (res?.versions && Array.isArray(res.versions)) {
      items = res.versions;
    }

    return items
      .filter((e) => {
        const type = e.entityType || e.dataFileType || e.fileType;
        return entityTypes.includes(type);
      })
      .map((e) => ({
        id: e._id,
        name: e.name || e.fullName,
        entityType: e.entityType || e.dataFileType || e.fileType || 'VERSION',
        kind: 'version' as const,
        createdBy: e.createdBy,
        createdAt: e.createdAt,
        isMasterCopy: e.isMasterCopy,
        controlCopies: e.controlCopies,
        raw: e,
      }));
  } catch (err) {
    console.error('[searchNotebook] Search failed:', { searchTerm, entityTypes, error: err });
    return [];
  }
}
